import styled, { css } from 'styled-components';
import { Divider, Flex, Link, styleMixinFocusOutline } from '@amzn/storm-ui';
import { AccordionLink } from '../styles';

type ThemedProps = {
  theme: {
    spacing: {
      xxsmall: { default: string };
      small: { default: string };
      base: { default: string };
    };
  };
};

const nestedGroupTextIndent = ({ theme }: ThemedProps): string =>
  theme.spacing.small.default;

const barToContentIndent = ({ theme }: ThemedProps): string =>
  theme.spacing.base.default;

const PARENT_TITLE_ICON_INLINE_START = 'xxsmall' as const;

const PARENT_TITLE_ICON_HALF_WIDTH_PX = 12;

const parentIconCenterFromColumn = ({ theme }: ThemedProps): string =>
  `calc(${theme.spacing[PARENT_TITLE_ICON_INLINE_START].default} + ${PARENT_TITLE_ICON_HALF_WIDTH_PX}px)`;

export const AllToolsSideNavLinkWrapper = styled.div`
  padding: '20px 40px', // TODO: replace with theme spacing tokens
  borderRadius: '8px', // TODO: replace with theme border radius token
  min-height: 32px;
  width: 296px;
  cursor: pointer;
  margin-block-end: ${({ theme }) => theme.spacing.xxxsmall.default};
`;

const sideNavLinkStyles = css<{ isSelected: boolean }>`
  box-sizing: border-box;
  gap: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.border.radius.large};
  cursor: pointer;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 32px;
  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};
  }
  :hover,
  :focus-visible {
    background-color: ${({ theme }) =>
      theme.selectableContainer.backgroundColor.hover};
  }
`;

export const AllToolsNavWrapper = styled.div`
  display: flex; // TODO: replace with the Flex component from StormUI
  justify-content: space-between;
  width: 100%;
`;

export const AllToolsSideNavLinkInnerWrapper = styled(Link)<{
  isSelected: boolean;
}>`
  border-radius: ${({ theme }) => theme.border.radius.large};
  ${sideNavLinkStyles};
  padding: 0 ${({ theme }) => theme.spacing.xxsmall.default};
  &:link,
  &:visited {
    color: ${({ theme }) => theme.text.color.default};
    text-decoration: none;
  }

  &:hover,
  &:active,
  &:focus-visible {
    text-decoration: none;
    color: ${({ theme }) =>
      theme.color
        .gray[800]}; // TODO: replace with the theme token so that this nav link looks fine in dark mode
    background-color: ${({ theme }) =>
      theme.selectableContainer.backgroundColor.hover};
  }
  &:focus-visible {
    ${styleMixinFocusOutline};
  }
  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.selectableContainer.backgroundColor.hover};
      font-weight: 700;
    `};
`;

export const AccordionContentInnerWrapper = styled.div`
  display: flex; // TODO: Replace with the Flex component from StormUI
  flex-direction: column;
  margin-block-start: ${({ theme }) => theme.spacing.xxxsmall.default};
  margin-inline-start: ${parentIconCenterFromColumn};
  padding-inline-start: ${barToContentIndent};
  border-inline-start: 2px solid
    ${({ theme }) => theme.border.color.subtle.default};
`;

export const AccordionContentGroupWrapper = styled(Flex)`
  box-sizing: border-box;
`;

export const NestedGroupIndent = styled.div<{ $show?: boolean }>`
  ${({ $show, theme }) =>
    $show &&
    css`
      margin-inline-start: ${nestedGroupTextIndent({ theme })};
      padding-inline-start: ${barToContentIndent({ theme })};
      border-inline-start: 2px solid ${theme.border.color.subtle.default};
    `}
`;

export const AccordionContentSubTitle = styled.span`
  // TODO: replace with Text component from StormUI
  // It is important to use StormUI components where possible since dark theme support is provided out of box

  color: ${({ theme }) => theme.text.color.default};

  font: ${({ theme }) => theme.font.text.base.default};

  padding: ${(props) =>
    `${props.theme.spacing.xxxsmall.default} 0 0 ${nestedGroupTextIndent(props)}`};
  margin-block-end: ${({ theme }) => theme.spacing.xxxsmall.default};
`;

export const StyledDivider = styled(Divider)`
  margin: ${({ theme }) =>
    `${theme.spacing.xxxxsmall.default} 12px ${theme.spacing.xxxxsmall.default} 0px`};
  width: 90%;
`;

export const AccordionLinkWrapper = styled.div<{
  isSelected: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  display: flex; // TODO: replace with the Flex component from StormUI
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.text.color.default};
  text-decoration: none;
  border-radius: ${({ theme }) => theme.border.radius.large};
  cursor: pointer;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 0;
  padding-inline: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} ${theme.spacing.xsmall.default}`};

  ${AccordionLink} {
    padding-block: ${({ theme }) => theme.spacing.xxsmall.default};
  }

  :hover,
  :focus-visible {
    background-color: ${({ theme }) =>
      theme.selectableContainer.backgroundColor.hover};
  }

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.selectableContainer.backgroundColor.selected};
    `};
`;

export const AllToolsTitleWrapper = styled(Flex).attrs({
  alignItems: 'center',
  paddingInlineStart: PARENT_TITLE_ICON_INLINE_START,
})`
  min-height: 32px;
`;
